import { Button } from "@mantine/core";
import { IconRoute } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { openPayingCustomersModal } from "@/components/paying-customers-only-modal";
import { useCreateServiceOrder } from "@/hooks/api-hooks";
import { SERVICE_ORDERS_PATH } from "@/lib/navigation";
import { isTier1Tenant } from "@/lib/format-utils";
import { ServiceOrder } from "@turizi/shared";

export interface NewServiceOrderComponentProps {
    onCreate?: (order: ServiceOrder) => void;
    onClose?: () => void;
}

export function useCreateNewServiceOrder() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const createServiceOrder = useCreateServiceOrder();

    const createNewServiceOrder = (callback?: (data: ServiceOrder) => void) => {
        if (!isTier1Tenant()) {
            openPayingCustomersModal();
            return;
        }
        createServiceOrder.mutate({}, {
            onSuccess: (data) => {
                if (data?.data) {
                    callback?.(data.data);
                    navigate(`${SERVICE_ORDERS_PATH}/${data.data.id}`);
                } else {
                    alert(t('error_creating_service_order'));
                }
            },
            onError: () => {
                alert(t('error_creating_service_order'));
            }
        });
    };

    return {
        createNewServiceOrder,
        isPending: createServiceOrder.isPending,
    };
}

export default function NewServiceOrderComponent({
    onCreate,
    onClose,
}: NewServiceOrderComponentProps): JSX.Element {
    const { t } = useTranslation();
    const { createNewServiceOrder, isPending } = useCreateNewServiceOrder();

    const handleCreate = () => {
        createNewServiceOrder((order) => {
            onCreate?.(order);
            onClose?.();
        });
    };

    return (
        <div className="p-4 space-y-4 max-w-md mx-auto text-center">
            <div className="flex justify-center text-indigo-600">
                <IconRoute size={48} stroke={1.5} />
            </div>
            <h3 className="text-lg font-semibold">{t('service_order')}</h3>
            <Button
                color="black"
                radius="md"
                fullWidth
                loading={isPending}
                onClick={handleCreate}
            >
                {t('create_service_order')}
            </Button>
        </div>
    );
}
